/**
 * API工具函数
 * 提供API响应处理、类型安全等工具
 */

import type { Result } from '../services/types';
import { logger } from './logger';

/** 与后端一致时等同 {@link Result}；解包逻辑另兼容 `message` 字段 */
export type ApiEnvelope<T = unknown> = Result<T>;

/**
 * 客户端本地构造的响应（测试/模拟）；真实接口以 {@link Result} 为准。
 */
export interface SyntheticApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
  success: boolean;
  timestamp: number;
}

/**
 * 解包API响应，提供类型安全
 * 处理常见的API响应格式，避免as any类型逃逸
 * 
 * @param response API响应对象
 * @returns 解包后的数据
 * @throws 当响应格式不符合预期时抛出错误
 */
export function unwrapApiResponse<T>(response: unknown): T {
  // 检查响应是否为对象
  if (typeof response !== 'object' || response === null) {
    logger.error('API响应不是对象:', response);
    throw new Error('API响应格式错误：响应不是对象');
  }

  const resp = response as Record<string, unknown>;
  
  // 检查响应是否包含data字段
  if (!('data' in resp)) {
    logger.error('API响应缺少data字段:', resp);
    throw new Error('API响应格式错误：缺少data字段');
  }

  // 检查响应码
  if ('code' in resp && typeof resp.code === 'number') {
    const code = resp.code;
    
    // 处理错误响应
    if (code !== 200 && code !== 0) {
      const message =
        typeof resp.message === 'string'
          ? resp.message
          : typeof resp.msg === 'string'
            ? resp.msg
            : '未知错误';
      logger.error(`API请求失败 [${code}]:`, message, resp);
      throw new Error(`API请求失败: ${message} (${code})`);
    }
  }

  // 检查success字段（如果存在）
  if ('success' in resp && typeof resp.success === 'boolean' && !resp.success) {
    const message =
      typeof resp.message === 'string'
        ? resp.message
        : typeof resp.msg === 'string'
          ? resp.msg
          : '请求失败';
    logger.error('API请求失败:', message, resp);
    throw new Error(`API请求失败: ${message}`);
  }

  // 返回数据，进行类型断言
  return resp.data as T;
}

/**
 * 安全解包API响应，不抛出异常
 * 当响应格式不符合预期时返回默认值
 * 
 * @param response API响应对象
 * @param defaultValue 默认值
 * @returns 解包后的数据或默认值
 */
export function safeUnwrapApiResponse<T>(response: unknown, defaultValue: T): T {
  try {
    return unwrapApiResponse<T>(response);
  } catch (error) {
    logger.warn('安全解包API响应失败，使用默认值:', error);
    return defaultValue;
  }
}

/**
 * 检查API响应是否成功
 * 
 * @param response API响应对象
 * @returns 是否成功
 */
export function isApiResponseSuccess(response: unknown): boolean {
  if (typeof response !== 'object' || response === null) {
    return false;
  }

  const resp = response as Record<string, unknown>;
  
  // 检查code字段
  if ('code' in resp && typeof resp.code === 'number') {
    return resp.code === 200 || resp.code === 0;
  }

  // 检查success字段
  if ('success' in resp && typeof resp.success === 'boolean') {
    return resp.success;
  }

  // 默认认为成功（兼容没有code/success字段的响应）
  return true;
}

/**
 * 获取API响应消息
 * 
 * @param response API响应对象
 * @returns 响应消息或默认消息
 */
export function getApiResponseMessage(response: unknown, defaultMessage: string = ''): string {
  if (typeof response !== 'object' || response === null) {
    return defaultMessage;
  }

  const resp = response as Record<string, unknown>;
  
  if ('message' in resp && typeof resp.message === 'string') {
    return resp.message;
  }

  if ('msg' in resp && typeof resp.msg === 'string') {
    return resp.msg;
  }

  return defaultMessage;
}

/**
 * 创建API响应对象
 * 
 * @param data 响应数据
 * @param options 选项
 * @returns API响应对象
 */
export function createApiResponse<T>(
  data: T,
  options: {
    code?: number;
    message?: string;
    success?: boolean;
  } = {}
): SyntheticApiResponse<T> {
  const {
    code = 200,
    message = 'success',
    success = true,
  } = options;

  return {
    code,
    message,
    data,
    success,
    timestamp: Date.now(),
  };
}

/**
 * 类型安全的API请求包装器
 * 
 * @param requestFn 请求函数
 * @returns 类型安全的响应数据
 */
export async function typedApiRequest<T>(
  requestFn: () => Promise<unknown>
): Promise<T> {
  try {
    const response = await requestFn();
    return unwrapApiResponse<T>(response);
  } catch (error) {
    logger.error('类型安全API请求失败:', error);
    throw error;
  }
}